<template>
  <CloseableDialog
    hash="custom_attribute_updater"
    max-width="900"
    id="attribute_updater"
    v-model="open"
    :disable-hash="true"
  >
    <template v-slot:header>
      <v-card-title>
        <h3 style="word-break: break-word">
          {{ t$(".headline") }}
        </h3>
      </v-card-title>
    </template>

    <v-form>
      <v-card-subtitle v-html="t$('.subtitle')"></v-card-subtitle>
      <v-card-text>
        <Header4 style="margin-bottom: 16px"
          >Eigenes Feld: {{ customAttribute.name }}</Header4
        >
        <v-file-input
          v-model="file"
          :rules="csvFileRules()"
          placeholder="CSV-Datei"
          outlined
        >
          CSV Datei
        </v-file-input>
        <Button
          :disabled="!file || !customAttribute || loading"
          :loading="loading"
          @click="uploadFile"
          color="primary"
        >
          Senden
        </Button>
      </v-card-text>
      <v-card-text v-if="loading"
        >Die Aktualisierung des Felder und ggfs. Umwandlung aller Werte kann bis
        zu einer Minuten dauern, bitte schließe dieses Fenster nicht. Danach
        kann es noch einige weitere Minuten dauern, bis die Änderungen überall
        sichtbar werden.
      </v-card-text>
      <v-card-text v-if="message" style="color: #ff6969">{{
        message
      }}</v-card-text>

      <v-card-text v-if="successCount > 0 || errors.length > 0">
        <h4>
          <span v-if="successCount > 0"
            >{{ t$(".success_count", { count: successCount }) }}.</span
          >
          <span v-if="errors.length > 0">
            {{ t$(".error", { count: errors.length }) }}
          </span>
        </h4>
        <ul style="padding-left: 16px">
          <li v-for="error in errors">
            {{
              customAttribute.attributable_type === "PersonalRecord"
                ? "Personen ID"
                : "Mitglieds ID"
            }}
            {{ error.record_id || "(leer)" }}:
            {{
              t$(`.errors.${error.error.code}`, {
                error:
                  error.error.message?.value?.join(", ") || error.error.message,
              })
            }}
          </li>
        </ul>
      </v-card-text>
    </v-form>
  </CloseableDialog>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue"
import {
  CustomAttributeBatchUpdateResponseRecordsNotUpdatedInnerError,
  CustomAttributeBatchUpdateResponseRecordsNotUpdatedInner,
  CustomAttributeDefinition,
  Level,
} from "suus-api"
import { AxiosError } from "axios"
import useTranslate from "features/common/translate/concerns/useTranslate"
import Button from "components/UI/Button.vue"
import Header from "components/UI/Header.vue"
import CloseableDialog from "components/UI/CloseableDialog.vue"
import Header4 from "app/ui-primitives/header/Header4.vue"
import { CustomAttributesClient } from "app/modules/custom-attributes/client"

export default defineComponent({
  name: "CustomAttributeValueUpdater",
  components: { Header4, CloseableDialog, Header, Button },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    customAttribute: {
      type: Object as PropType<CustomAttributeDefinition>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const selectedLevel = ref<Level | null>(null)
    const file = ref<File | null>(null)
    const { t$ } = useTranslate("components.custom_attribute_batch_updater")

    const open = ref(props.value)
    watch(
      () => props.value,
      (value) => {
        open.value = value
      }
    )
    watch(
      () => open.value,
      (value) => {
        emit("input", value)
      }
    )

    const message = ref("")
    const loading = ref(false)
    const successCount = ref(0)
    const errors = ref(
      [] as CustomAttributeBatchUpdateResponseRecordsNotUpdatedInner[]
    )
    const uploadFile = async () => {
      try {
        loading.value = true
        message.value = ""
        successCount.value = 0
        errors.value = []

        const response = await CustomAttributesClient.batchUpdate(
          props.customAttribute.id,
          file.value
        )
        if ("data" in response) {
          const data = response.data
          errors.value = data.records_not_updated
          successCount.value = data.updated_records_count
          emit("update-in-progress")
        } else {
          message.value =
            "Es ist ein Fehler aufgetreten (" + response.statusText + ")"
        }
      } catch (error) {
        // Check type if an axios error
        if ((error as AxiosError).isAxiosError) {
          if ((error as AxiosError).response.status === 423) {
            message.value =
              "Es ist bereits ein Update-Vorgang dieses Feldes im Gange, bitte probiere es später erneut."
            console.warn(error)
          } else {
            const errData = (error as AxiosError).response
              .data as CustomAttributeBatchUpdateResponseRecordsNotUpdatedInnerError
            message.value = `Server-Fehler beim Aktualisieren der Einträge: ${errData.error} (${errData.code}): ${errData.message}`
            console.error(error)
          }
        } else {
          message.value = `Unspezifzierter Fehler beim Aktualisieren der Einträge: ${error}`
          console.error(error)
        }
      } finally {
        loading.value = false
      }
    }

    return {
      selectedLevel,
      file,
      uploadFile,
      message,
      loading,
      successCount,
      errors,
      t$,
      open,
    }
  },
  methods: {
    csvFileRules() {
      return [
        (v: File) => !!v || "Eine Datei muss ausgewählt werden.",
        (v: File) =>
          v?.type === "text/csv" || "Die Datei muss eine CSV-Datei sein.",
        (v: File) => v?.size < 1000000 || "Die Datei ist größer als 1MB.",
      ]
    },
  },
})
</script>
